'use client';

import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  ControlBar,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  CarouselLayout,
  useTracks,
  Chat,
  useLayoutContext,
  MessageFormatter,
  LayoutContextProvider,
  RoomAudioRenderer,
  VideoTrack,
  useParticipants,
  useRoomContext,
} from '@livekit/components-react';
import { CustomParticipantTile } from './CustomParticipantTile';
import { Track, RoomEvent, type Room } from 'livekit-client';
import { useLayoutView, LayoutViewProvider } from './LayoutContext';
import { LayoutSettingsModal } from './LayoutSettingsModal';
import toast from 'react-hot-toast';
import { CONTROL_EVENTS_TOPIC } from './useGlobalSpotlight';
import { PinningProvider, usePinning } from './PinningContext';
import {
  buildGridPage,
  shouldPreferCompactLayout,
  sortGridTracks,
  suggestedMaxTiles,
} from './gridLayoutUtils';
import { useHostSettings } from './HostSettingsContext';
import { useFileRecordingActive } from './useFileRecordingActive';

/**
 * Mantém um Set estável de identities de oradores ativos com debounce de `stickyMs`.
 * Evita que flutuações rápidas de áudio reordenem os tiles do mosaico a cada fração de segundo.
 */
function useStickyActiveSpeakers(room: Room | undefined, stickyMs = 1500): Set<string> {
  const [stableIds, setStableIds] = useState<Set<string>>(
    () => new Set((room?.activeSpeakers ?? []).map((p) => p.identity)),
  );
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!room) return;

    const update = () => {
      const newIds = new Set((room.activeSpeakers ?? []).map((p) => p.identity));
      if (pendingRef.current) clearTimeout(pendingRef.current);
      pendingRef.current = setTimeout(() => setStableIds(newIds), stickyMs);
    };

    room.on('activeSpeakersChanged', update);
    return () => {
      room.off('activeSpeakersChanged', update);
      if (pendingRef.current) clearTimeout(pendingRef.current);
    };
  }, [room, stickyMs]);

  return stableIds;
}

function formatRecordingDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const HostRecordingTimer = React.memo(function HostRecordingTimer() {
  const { isHost } = useHostSettings();
  const room = useRoomContext();
  const isRecording = useFileRecordingActive(room?.name);
  const [recordingStartedAt, setRecordingStartedAt] = useState<number | null>(null);
  const [durationLabel, setDurationLabel] = useState('0:00');

  useEffect(() => {
    const onStart = () => {
      setRecordingStartedAt((prev) => prev ?? Date.now());
    };
    const onStop = () => {
      setRecordingStartedAt(null);
      setDurationLabel('0:00');
    };

    window.addEventListener('recording:start', onStart);
    window.addEventListener('recording:stop', onStop);
    return () => {
      window.removeEventListener('recording:start', onStart);
      window.removeEventListener('recording:stop', onStop);
    };
  }, []);

  useEffect(() => {
    if (isRecording) {
      setRecordingStartedAt((prev) => prev ?? Date.now());
      return;
    }
    setRecordingStartedAt(null);
    setDurationLabel('0:00');
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording || recordingStartedAt === null) return;

    const tick = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - recordingStartedAt) / 1000));
      setDurationLabel(formatRecordingDuration(elapsed));
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [isRecording, recordingStartedAt]);

  if (!isHost || !isRecording || recordingStartedAt === null) {
    return null;
  }

  return (
    <div className="em-glass-sm flex items-center gap-2 px-2.5 py-1.5 rounded-full">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
      <span className="text-[11px] text-white/70 tabular-nums">{durationLabel}</span>
    </div>
  );
});

// CONTEÚDO PRINCIPAL
function ConferenceContent({ chatMessageFormatter }: { chatMessageFormatter?: MessageFormatter }) {
  const {
    layoutMode,
    setLayoutMode,
    maxTiles,
    setMaxTiles,
    maxTilesUserOverride,
    hideNonVideo,
    openLayoutModal,
  } = useLayoutView();
  const layoutContext = useLayoutContext();
  const room = useRoomContext();
  const participants = useParticipants();
  const { pinnedTrackRef, setPinnedTrackRef } = usePinning();
  const lastActiveSpeakerIdRef = useRef<string | null>(null);
  const [globalSpotlightTarget, setGlobalSpotlightTarget] = useState<string | null>(null);
  const [gridPage, setGridPage] = useState(0);
  const stableActiveSpeakers = useStickyActiveSpeakers(room);
  const compactLayoutHintShown = useRef(false);
  const prevSuggestedTilesRef = useRef<number | null>(null);

  useEffect(() => {
    setGridPage(0);
  }, [maxTiles]);

  useEffect(() => {
    if (maxTilesUserOverride) return;

    const count = participants.length;
    const suggested = suggestedMaxTiles(count);
    const prev = prevSuggestedTilesRef.current;
    setMaxTiles(suggested);
    if (prev !== null && suggested === 6 && prev !== 6 && count > 20) {
      toast('Layout ajustado para melhorar a performance com muitos participantes.', {
        icon: 'ℹ️',
        duration: 5000,
      });
    }
    prevSuggestedTilesRef.current = suggested;
  }, [participants.length, maxTilesUserOverride, setMaxTiles]);

  useEffect(() => {
    const count = participants.length;
    if (shouldPreferCompactLayout(count) && layoutMode === 'auto' && !compactLayoutHintShown.current) {
      compactLayoutHintShown.current = true;
      toast(
        'Sala com muitos participantes — use as setas na parte inferior do mosaico para ver todos.',
        { icon: 'ℹ️', duration: 5000 },
      );
    }
  }, [participants.length, layoutMode]);

  // 1. Pega TODOS os tracks
  const allTracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Detecta active speaker e atualiza o último que falou
  useEffect(() => {
    if (!room) return;

    const updateActiveSpeaker = () => {
      const activeSpeakers = room.activeSpeakers || [];
      if (activeSpeakers.length > 0) {
        lastActiveSpeakerIdRef.current = activeSpeakers[0].identity;
      }
    };

    // Atualiza quando há mudanças nos active speakers
    room.on('activeSpeakersChanged', updateActiveSpeaker);
    updateActiveSpeaker(); // Chama imediatamente

    return () => {
      room.off('activeSpeakersChanged', updateActiveSpeaker);
    };
  }, [room]);

  // Listener para mensagens de Spotlight Global
  useEffect(() => {
    if (!room) return;

    const handleDataReceived = (
      payload: Uint8Array,
      participant?: { identity?: string; name?: string | null },
      kind?: any,
      topic?: string
    ) => {
      // Verifica se é o tópico correto
      if (topic !== CONTROL_EVENTS_TOPIC) return;

      try {
        const message = JSON.parse(new TextDecoder().decode(payload));

        if (message.action === 'GLOBAL_SPOTLIGHT' && message.targetIdentity) {
          // Encontra o track do participante alvo
          const targetTrack = allTracks.find(
            (t) =>
              t.participant.identity === message.targetIdentity &&
              t.source === Track.Source.Camera
          );

          if (targetTrack) {
            setGlobalSpotlightTarget(message.targetIdentity);
            setPinnedTrackRef(targetTrack);
            setLayoutMode('spotlight');

            // Mostra notificação apenas se não for o próprio usuário que enviou
            if (participant?.identity !== room.localParticipant?.identity) {
              const targetName =
                targetTrack.participant.name || targetTrack.participant.identity;
              toast.success(`O anfitrião destacou o vídeo de ${targetName}`, {
                duration: 3000,
              });
            }
          }
        } else if (message.action === 'REMOVE_SPOTLIGHT') {
          setGlobalSpotlightTarget(null);
          setPinnedTrackRef(null);
          // Volta para o modo automático ou grid
          if (layoutMode === 'spotlight') {
            setLayoutMode('auto');
          }

          // Mostra notificação apenas se não for o próprio usuário que enviou
          if (participant?.identity !== room.localParticipant?.identity) {
            toast('Destaque removido pelo anfitrião', {
              icon: 'ℹ️',
              duration: 2000,
            });
          }
        }
      } catch (error) {
        console.error('Erro ao processar mensagem de spotlight global:', error);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room, allTracks, layoutMode, setLayoutMode]);

  // Pin manual via LayoutSettingsModal / spotlight global — sem polling DOM.
  useEffect(() => {
    if (layoutMode === 'spotlight' && pinnedTrackRef) {
      return;
    }
    if (layoutMode !== 'grid') {
      setPinnedTrackRef(null);
    }
  }, [layoutMode, pinnedTrackRef, setPinnedTrackRef]);

  // 2. Filtros e Lógica de Layout
  const {
    viewMode,
    filteredTracks,
    focusTrack,
    carouselTracks,
    totalGridPages,
    safeGridPage,
    allGridTracksCount,
    gridRangeStart,
    gridRangeEnd,
  } = useMemo(() => {
    
    // Filtro: Ocultar sem vídeo (mas NÃO no modo spotlight - sempre mostra o focado)
    let processedTracks = allTracks;
    if (hideNonVideo && layoutMode !== 'spotlight') {
      processedTracks = allTracks.filter(t => 
        t.source === Track.Source.ScreenShare || // Sempre mostra tela
        (t.source === Track.Source.Camera && !t.participant.isCameraEnabled) === false // Mostra apenas se camera ON
      );
    }

    // Detecta Screen Share
    const screenShare = processedTracks.find(t => t.source === Track.Source.ScreenShare) || null;
    
    // NOVA LÓGICA DE PRIORIDADE PARA TARGET FOCUS
    // 1. Screen Share (Prioridade Máxima)
    // 2. Global Spotlight (Spotlight for Everyone - prioridade sobre pinned local)
    // 3. Pinned Participant (Se o usuário fixou alguém manualmente)
    // 4. Active Speaker (Quem está falando no momento)
    // 5. Last Active Speaker (Se houver silêncio, mantém o último que falou)
    // 6. Primeiro Participante REMOTO (Fallback - evita mostrar o usuário local)
    // 7. Último recurso (Local ou qualquer um)
    
    let targetFocus: typeof allTracks[0] | null = 
      // 1º: Tela Compartilhada
      screenShare ||
      
      // 2º: Global Spotlight (Spotlight for Everyone - prioridade máxima após screen share)
      (globalSpotlightTarget
        ? allTracks.find(
            (t) =>
              t.participant.identity === globalSpotlightTarget &&
              t.source === Track.Source.Camera
          ) || null
        : null) ||
      
      // 3º: Usuário Pinado Manualmente (pinned local)
      pinnedTrackRef ||
      
      // 3º: Orador Ativo (Active Speaker) - procura quem está falando
      (() => {
        if (!room) return null;
        const activeSpeakers = room.activeSpeakers || [];
        if (activeSpeakers.length > 0) {
          const activeSpeakerId = activeSpeakers[0].identity;
          return processedTracks.find(
            t => t.participant.identity === activeSpeakerId && 
                 t.source === Track.Source.Camera
          ) || null;
        }
        return null;
      })() ||
      
      // 4º: Last Active Speaker (mantém o último que falou se houver silêncio)
      (lastActiveSpeakerIdRef.current
        ? processedTracks.find(
            (t) =>
              t.participant.identity === lastActiveSpeakerIdRef.current &&
              t.source === Track.Source.Camera,
          ) || null
        : null) ||
      
      // 5º: Fallback - Primeiro Participante REMOTO (Evita mostrar eu mesmo)
      processedTracks.find(
        t => !t.participant.isLocal && 
             t.source === Track.Source.Camera
      ) ||
      
      // 6º: Último recurso (Eu mesmo ou qualquer um)
      processedTracks.find(t => t.source === Track.Source.Camera) ||
      null;
    
    // CRUCIAL: No modo spotlight, se temos um track focado, sempre mantém ele
    // mesmo que a câmera seja desligada (o placeholder será mostrado)
    if (layoutMode === 'spotlight' && targetFocus) {
      // Garante que o track focado está sempre incluído, mesmo sem câmera
      const focusedTrackInAll = allTracks.find(
        t => t.participant.identity === targetFocus!.participant.identity && 
             t.source === targetFocus!.source
      );
      if (focusedTrackInAll) {
        targetFocus = focusedTrackInAll; // Usa o track original com placeholder
      }
    }

    // Lógica do Modo AUTOMÁTICO
    let effectiveMode = layoutMode;
    if (layoutMode === 'auto') {
      // Se tem tela compartilhada -> Barra Lateral
      // Se não -> Mosaico
      effectiveMode = screenShare ? 'sidebar' : 'grid';
    }

    // Prepara dados para Sidebar/Spotlight
    const others = targetFocus 
      ? processedTracks.filter(t => t.participant.identity !== targetFocus.participant.identity || t.source !== targetFocus.source)
      : processedTracks;

    // Mosaico: todos os participantes com paginação (blocos por página = maxTiles)
    // sortGridTracks usa stableActiveSpeakers (debounced) para evitar reordenação frequente
    const gridPagination =
      effectiveMode === 'grid'
        ? buildGridPage(sortGridTracks(processedTracks, stableActiveSpeakers), maxTiles, gridPage)
        : buildGridPage(processedTracks, processedTracks.length || 1, 0);

    return {
      viewMode: effectiveMode,
      filteredTracks: gridPagination.tracksForPage,
      focusTrack: targetFocus,
      carouselTracks: others,
      totalGridPages: gridPagination.totalPages,
      safeGridPage: gridPagination.safePage,
      allGridTracksCount: gridPagination.totalTracks,
      gridRangeStart: gridPagination.rangeStart,
      gridRangeEnd: gridPagination.rangeEnd,
    };
  }, [
    allTracks,
    layoutMode,
    hideNonVideo,
    maxTiles,
    gridPage,
    pinnedTrackRef,
    room,
    globalSpotlightTarget,
    stableActiveSpeakers,
  ]);

  return (
    <div className="lk-video-conference relative h-screen overflow-hidden bg-[#0b0b14]">

      <RoomAudioRenderer />

      {/* ── TOP INFO BAR ── */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 pointer-events-none">
        <div className="flex items-center gap-2 pointer-events-auto">
          <HostRecordingTimer />
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 em-glass-sm px-4 py-1.5 rounded-full">
          <span className="text-xs font-medium text-white/45">{room?.name || ''}</span>
        </div>

        <div className="flex items-center gap-1.5 pointer-events-auto">
          <button
            onClick={openLayoutModal}
            className="w-[34px] h-[34px] rounded-[9px] em-glass-sm flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors"
            title="Alterar layout"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
          </button>
        </div>
      </div>

      <div className="lk-video-conference-inner flex flex-col h-full pt-[52px]">
        <div className="flex-1 min-h-0 relative overflow-hidden px-2 pb-[88px]">
        {/* Grid mode */}
        {viewMode === 'grid' && (
          <>
            <GridLayout tracks={filteredTracks}>
              <CustomParticipantTile />
            </GridLayout>
            {totalGridPages > 1 && (
              <div
                className="evolumeet-grid-pagination"
                style={{
                  position: 'absolute',
                  // Fica acima da barra de controle (~88px) + margem de 12px = 100px do fundo
                  bottom: 100,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  // z-index abaixo da barra de controle para não bloquear cliques
                  zIndex: 20,
                  background: 'rgba(0,0,0,0.65)',
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                <button
                  type="button"
                  className="lk-button lk-button-icon"
                  disabled={safeGridPage <= 0}
                  onClick={() => setGridPage((p) => Math.max(0, p - 1))}
                  title="Participantes anteriores"
                  aria-label="Participantes anteriores"
                >
                  ‹
                </button>
                <span style={{ color: '#eee', minWidth: 120, textAlign: 'center' }}>
                  {gridRangeStart}–{gridRangeEnd} de {allGridTracksCount}
                </span>
                <button
                  type="button"
                  className="lk-button lk-button-icon"
                  disabled={safeGridPage >= totalGridPages - 1}
                  onClick={() => setGridPage((p) => Math.min(totalGridPages - 1, p + 1))}
                  title="Próximos participantes"
                  aria-label="Próximos participantes"
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}

        {/* Sidebar mode */}
        {viewMode === 'sidebar' && (
          <div
            data-layout-mode={viewMode}
            style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <FocusLayoutContainer>
              <CarouselLayout tracks={carouselTracks}>
                <CustomParticipantTile />
              </CarouselLayout>
              {focusTrack ? (
                <FocusLayout trackRef={focusTrack} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20">
                  <p>Aguardando vídeo...</p>
                </div>
              )}
            </FocusLayoutContainer>
          </div>
        )}

        {/* Spotlight mode */}
        {viewMode === 'spotlight' && focusTrack && (
          <div
            data-layout-mode={viewMode}
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#000',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              {focusTrack.publication?.isSubscribed && focusTrack.publication?.track ? (
                <VideoTrack
                  trackRef={focusTrack}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '24px',
                  }}
                >
                  <div
                    style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      backgroundColor: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '48px',
                      marginBottom: '16px',
                    }}
                  >
                    {focusTrack.participant.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>{focusTrack.participant.name || focusTrack.participant.identity}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === 'spotlight' && !focusTrack && (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <p>Aguardando vídeo...</p>
          </div>
        )}
        </div>

        <ControlBar
          controls={{
            chat: true,
            screenShare: true,
            leave: true,
            microphone: true,
            camera: true,
            settings: false,
          }}
        />
      </div>

      <div style={{ display: layoutContext?.widget.state?.showChat ? 'contents' : 'none' }}>
        <Chat messageFormatter={chatMessageFormatter} />
      </div>

      <LayoutSettingsModal />
    </div>
  );
}

export function EvoluVideoConference(props: { chatMessageFormatter?: MessageFormatter, SettingsComponent?: React.ComponentType }) {
  return (
    <LayoutViewProvider>
      <PinningProvider>
        <LayoutContextProvider>
          <ConferenceContent chatMessageFormatter={props.chatMessageFormatter} />
        </LayoutContextProvider>
      </PinningProvider>
    </LayoutViewProvider>
  );
}